import { toast as sonnerToast } from "sonner";
import type { ReactNode } from "react";

type ToastVariant = "success" | "error" | "warning" | "info";

const VARIANT_CLASS: Record<ToastVariant, string> = {
	success: "toast-success",
	error: "toast-error",
	warning: "toast-warning",
	info: "toast-info",
};

type ToastOptions = {
	description?: ReactNode;
	action?: { label: string; onClick: () => void };
	duration?: number;
	id?: string | number;
};

function renderBody(title: string, description?: ReactNode) {
	return (
		<div>
			<div className="toast-title">{title}</div>
			{description && <div className="toast-description">{description}</div>}
		</div>
	);
}

function showToast(variant: ToastVariant, title: string, options: ToastOptions = {}) {
	const body = renderBody(title, options.description);
	const className = VARIANT_CLASS[variant];
	const fn = sonnerToast[variant];
	const opts: Parameters<typeof fn>[1] = { className };
	if (options.action) opts.action = options.action;
	return fn(body, opts);
}

export const toast = {
	success: (title: string, options?: ToastOptions) =>
		showToast("success", title, options),
	error: (title: string, options?: ToastOptions) =>
		showToast("error", title, options),
	warning: (title: string, options?: ToastOptions) =>
		showToast("warning", title, options),
	info: (title: string, options?: ToastOptions) =>
		showToast("info", title, options),
	loading: (title: string, options?: ToastOptions) => {
		const body = renderBody(title, options?.description);
		const opts: Parameters<typeof sonnerToast.loading>[1] = {
			className: "toast-loading",
		};
		if (options?.id !== undefined) opts.id = options.id;
		if (options?.duration !== undefined) opts.duration = options.duration;
		return sonnerToast.loading(body, opts);
	},
	dismiss: (id?: string | number) => sonnerToast.dismiss(id),
	promise: sonnerToast.promise,
};
